import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckSquare, ClipboardList, History, Users, FileText, LayoutTemplate, ChevronLeft, ChevronRight, X, Settings, Shield, BarChart3, UserCog, AlertCircle, FileSpreadsheet, Database, Gauge } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
}
interface NavSection {
  title?: string;
  items: NavItem[];
}
interface SidebarProps {
  onClose?: () => void;
}
export const Sidebar = ({
  onClose
}: SidebarProps) => {
  const {
    user
  } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navSections: NavSection[] = [{
    items: [{
      name: 'My Tasks',
      path: '/',
      icon: <CheckSquare size={20} data-id="element-51" />,
      roles: ['employee', 'manager', 'admin']
    }, {
      name: 'Task History',
      path: '/history',
      icon: <History size={20} data-id="element-52" />,
      roles: ['employee', 'manager', 'admin']
    }]
  }, {
    title: 'Management',
    items: [{
      name: 'Employees',
      path: '/employees',
      icon: <Users size={20} data-id="element-53" />,
      roles: ['manager', 'admin']
    }, {
      name: 'Assign Tasks',
      path: '/assign',
      icon: <ClipboardList size={20} data-id="element-54" />,
      roles: ['manager', 'admin']
    }, {
      name: 'Task Templates',
      path: '/templates',
      icon: <LayoutTemplate size={20} data-id="element-55" />,
      roles: ['manager', 'admin']
    }]
  }, {
    title: 'Administration',
    items: [{
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: <Gauge size={20} data-id="element-56" />,
      roles: ['admin']
    }, {
      name: 'User Management',
      path: '/admin/users',
      icon: <UserCog size={20} data-id="element-57" />,
      roles: ['admin']
    }, {
      name: 'Departments',
      path: '/admin/departments',
      icon: <FileSpreadsheet size={20} data-id="element-58" />,
      roles: ['admin']
    }, {
      name: 'Analytics',
      path: '/admin/analytics',
      icon: <BarChart3 size={20} data-id="element-59" />,
      roles: ['admin']
    }, {
      name: 'System Logs',
      path: '/admin/logs',
      icon: <AlertCircle size={20} data-id="element-60" />,
      roles: ['admin']
    }, {
      name: 'Data Management',
      path: '/admin/data',
      icon: <Database size={20} data-id="element-61" />,
      roles: ['admin']
    }, {
      name: 'Security',
      path: '/admin/security',
      icon: <Shield size={20} data-id="element-62" />,
      roles: ['admin']
    }, {
      name: 'System Settings',
      path: '/admin/settings',
      icon: <Settings size={20} data-id="element-63" />,
      roles: ['admin']
    }]
  }];
  // Filter sections based on user role
  const filteredSections = navSections.map(section => {
    const filteredItems = section.items.filter(item => user ? item.roles.includes(user.role) : false);
    return {
      ...section,
      items: filteredItems
    };
  }).filter(section => section.items.length > 0);
  return <div className={`h-screen bg-surface-50 border-r border-border transition-all duration-300 flex flex-col ${collapsed ? 'w-16' : 'w-64'}`} data-id="element-64">
      <div className="flex items-center justify-between h-16 px-4 border-b border-border" data-id="element-65">
        <div className={`flex items-center ${collapsed ? 'justify-center w-full' : ''}`} data-id="element-66">
          <FileText className="h-6 w-6 text-brand-600" data-id="element-67" />
          {!collapsed && <span className="ml-2 font-semibold text-lg" data-id="element-68">TaskFiles</span>}
        </div>
        <div className="flex" data-id="element-69">
          {onClose && <button onClick={onClose} className="p-1 rounded-full hover:bg-surface-100 text-text-secondary md:hidden" aria-label="Close sidebar" data-id="element-70">
              <X size={18} data-id="element-71" />
            </button>}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1 rounded-full hover:bg-surface-100 text-text-secondary hidden md:block" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-72">
            {collapsed ? <ChevronRight size={18} data-id="element-73" /> : <ChevronLeft size={18} data-id="element-74" />}
          </button>
        </div>
      </div>
      <nav className="mt-4 flex-1 overflow-y-auto" data-id="element-75">
        {filteredSections.map((section, index) => <div key={index} className="mb-4" data-id="element-76">
            {section.title && !collapsed && <h3 className="px-4 mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary" data-id="element-77">
                {section.title}
              </h3>}
            <ul className="space-y-1 px-2" data-id="element-78">
              {section.items.map(item => <li key={item.path} data-id="element-79">
                  <Link to={item.path} className={`flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors duration-150 ${collapsed ? 'justify-center' : ''} ${location.pathname === item.path ? 'bg-brand-50 text-brand-700 font-medium' : 'text-text-secondary hover:bg-surface-100'}`} onClick={onClose} data-id="element-80">
                    <span className={collapsed ? '' : 'flex-shrink-0'} data-id="element-81">
                      {item.icon}
                    </span>
                    {!collapsed && <span className="ml-3 truncate" data-id="element-82">{item.name}</span>}
                  </Link>
                </li>)}
            </ul>
          </div>)}
      </nav>
      <div className="mt-auto mb-4 px-4" data-id="element-83">
        <div className={`p-3 rounded-lg ${user?.role === 'admin' ? 'bg-brand-50/70 border border-brand-200' : 'bg-brand-50'} ${collapsed ? 'flex justify-center' : ''}`} data-id="element-84">
          {collapsed ? <FileText size={20} className={`${user?.role === 'admin' ? 'text-brand-700' : 'text-brand-600'}`} data-id="element-85" /> : <div data-id="element-86">
              <p className={`text-xs font-medium ${user?.role === 'admin' ? 'text-brand-800' : 'text-brand-800'}`} data-id="element-87">
                TaskFiles v1.0
              </p>
              <p className={`text-xs ${user?.role === 'admin' ? 'text-brand-700' : 'text-brand-600'} mt-1`} data-id="element-88">
                {user?.role === 'admin' ? 'Admin Console' : 'Enterprise Edition'}
              </p>
            </div>}
        </div>
      </div>
    </div>;
};